import React from 'react';

// A full-bleed campaign photograph used as a section's ground.
//
// The first attempt at putting this campaign on the site mounted the finished
// posters in a framed three-up grid, and it read as a case of adverts bolted to
// the homepage: a poster is a self-contained object with its own headline and
// lockup, and putting the loreto.io lockup on loreto.io is circular. So these
// use the raw portraits — no burned-in type, no frame, no caption — and the
// page sets its own serif over them.
//
// The photo is masked rather than overlaid. Covering its edge with an opaque
// panel still leaves a visible vertical seam where the two grounds meet; masking
// the image away means there is no edge left to see. The only campaign element
// that survives is the ink on the back of the hand, which a reader finds rather
// than is shown.

export default function PhotoStatement({
  num,
  eyebrow,
  image,
  focus = '58% 32%',
  side = 'right',
  // A literal Tailwind class, not a computed value: the JIT compiler scans
  // source text, so an arbitrary runtime string would never get generated.
  insetClass = 'md:left-[34%]',
  shortFade = false,
  // Drops the top rule. The nav already draws a line, so the section that
  // opens the page must not draw a second one against it.
  flush = false,
  overlay,
  headline,
  children,
}) {
  const right = side === 'right';
  return (
    <section className={`relative ${flush ? '' : 'border-t border-[var(--border)]'} bg-[var(--bg)] overflow-hidden`}>
      <div
        className={`photo-bleed ${shortFade ? 'photo-bleed--short' : ''} ${
          right ? '' : 'photo-bleed-left'
        } absolute inset-0 ${insetClass} bg-cover bg-no-repeat`}
        style={{
          backgroundImage: `url(${image})`,
          backgroundPosition: focus,
          // On the section that opens the page the nav sits on top of the
          // photograph, and a subject near the top of the frame disappears
          // under it. background-position cannot rescue that — cover leaves
          // only ~60px of vertical slack and the nav is 64px tall — so the
          // photo layer starts where the nav ends instead. The strip it gives
          // up is the nav's own dark background, so nothing shows.
          //
          // The other half of that fix lives in the asset: harness-wide.jpg is
          // a 2:1 canvas, wider than any hero box here, so `cover` binds on
          // HEIGHT and the subject is never cropped top or bottom again. Feed
          // this a 3:2 photograph and the old problem returns.
          ...(flush ? { top: '4rem' } : {}),
        }}
        aria-hidden="true"
      />
      {/* Holds the copy side down and takes the glare off the brightest part of
          the frame, without introducing a second hard edge. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            overlay ||
            `linear-gradient(${right ? '90deg' : '270deg'}, var(--bg) 0%, rgba(10,10,10,0.90) 30%, rgba(10,10,10,0.55) 48%, rgba(10,10,10,0.30) 70%, rgba(10,10,10,0.30) 100%)`,
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-44">
        <div className={`max-w-[46ch] md:max-w-[40ch] ${right ? '' : 'md:ml-auto'}`}>
          <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
            <span className="divider-num">({num})</span>
            <span className="eyebrow">{eyebrow}</span>
          </div>
          <h2 className="serif text-[2.6rem] md:text-[4rem] leading-[1.04] tracking-[-0.02em] mb-8">
            {headline}
          </h2>
          <div className="text-[var(--text-2)] text-[1.02rem] leading-[1.7] space-y-6">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}
